// C:\Users\cornn\source\repos\purehealthnexus\src\components\payments\CheckoutSuccess.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../firebase/AuthContext";
import { db } from "../../firebase/firebaseServices";
import "./paymentsCss/checkoutSuccess.css";
import SubscriptionInvoicePdfDownLoad from "../DocumentationDownload/subscriptionInvoicePdfDownLoad";

import {
  mapPayPalToSubPayload,
  writeUserSubscription,
} from "./PayPalSubscriptionService";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

// Mapping of Plan IDs to human-readable names
const PLAN_ID_TO_NAME = {
  [process.env.REACT_APP_PAYPAL_PLAN_ID_MONTHLY]: "Flex (Monthly)",
  [process.env.REACT_APP_PAYPAL_PLAN_ID_6M]: "Committed (6 Months)",
  [process.env.REACT_APP_PAYPAL_PLAN_ID_12M]: "Dedicated (12 Months)",
};

// Formats a number into a currency string (e.g., 20 => "$20.00")
function formatMoney(value, currency = "USD") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(numericValue);
  } catch {
    return `$${numericValue.toFixed(2)}`;
  }
}

// Formats an ISO-like date string into a local date and time string
function formatDate(isoLike) {
  if (!isoLike) return "-";
  const date = new Date(isoLike);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

// Masks an email address for privacy (e.g., "user@example.com" => "u***r@example.com")
function maskEmail(email) {
  if (!email || typeof email !== "string") return "-";
  const [userPart, domain] = email.split("@");
  if (!domain) return email;

  const maskedUserPart =
    userPart.length <= 2
      ? userPart[0]
      : `${userPart[0]}${"*".repeat(Math.min(userPart.length - 2, 6))}${userPart.slice(
          -1
        )}`;

  return `${maskedUserPart}@${domain}`;
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const { currentUser } = useAuth();

  const subId = params.get("subId");
  const urlPlanId = params.get("planId") || undefined;

  const planHint = useMemo(
    () => ({
      name: location.state?.name || "Your plan",
      price: location.state?.price,
    }),
    [location.state]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);
  const [debugBody, setDebugBody] = useState("");
  const [debugStatus, setDebugStatus] = useState(null);

  // State guard to prevent duplicate writes to Firestore.
  const [isWriteComplete, setIsWriteComplete] = useState(false);

  useEffect(() => {
    document.getElementById("main-heading")?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Guard against running if there's no subscription ID or if the write is already done.
      if (!subId || isWriteComplete) {
        if (!subId) {
          setErr("Missing subscription id.");
          setLoading(false);
        }
        return;
      }

      try {
        const endpoint = `${API_BASE}/api/paypal/subscriptions/${encodeURIComponent(
          subId
        )}`;
        const response = await fetch(endpoint, {
          headers: { Accept: "application/json" },
        });

        const bodyText = await response.text();
        setDebugStatus(response.status);

        let parsedResponse;
        try {
          parsedResponse = JSON.parse(bodyText);
        } catch {
          setDebugBody(bodyText.slice(0, 1200));
          throw new Error(
            `Non-JSON response from backend (status ${response.status}).`
          );
        }

        if (!response.ok) {
          setDebugBody(bodyText.slice(0, 1200));
          throw new Error(`Server ${response.status}`);
        }

        const planId = parsedResponse.plan_id || urlPlanId || null;
        const planName =
          parsedResponse.plan_name ||
          (planId ? PLAN_ID_TO_NAME[planId] : null) ||
          planHint.name ||
          null;

        const details = {
          id: parsedResponse.id,
          status: parsedResponse.status,
          planId,
          planName,
          currency: parsedResponse.currency_code || "USD",
          price: parsedResponse.price,
          start: parsedResponse.start_time || parsedResponse.create_time,
          nextBilling: parsedResponse.next_billing_time || null,
          invoiceId: parsedResponse.latest_invoice_id || null,
          payerEmail:
            parsedResponse.payer_email_masked ||
            parsedResponse.payer_email ||
            currentUser?.email ||
            null,
        };

        if (!cancelled) {
          setInfo(details);
        }

        if (currentUser?.uid) {
          const planMetaForMap = {
            planId: details.planId,
            displayName: details.planName,
            price: details.price,
            currency: details.currency,
          };

          const subscriptionPayload = mapPayPalToSubPayload(
            parsedResponse,
            planMetaForMap
          );

          // Transaction: writes subscriptions and conditionally updates ROOT isTrialPeriodActive.
          await writeUserSubscription(db, currentUser.uid, subscriptionPayload);

          // Prevent any re-entrancy from re-writing.
          if (!cancelled) {
            setIsWriteComplete(true);
          }

          // Session flag is only local; not a Firestore write.
          try {
            sessionStorage.setItem("phn:subActiveSession", "1");
          } catch {
            // Ignore storage errors (e.g., private mode)
          }
        }
      } catch (subscriptionError) {
        // Mark error as used to avoid lint warnings, without logging to console.
        String(subscriptionError);
        if (!cancelled) {
          setErr(
            "We couldn't load your subscription details. Your payment may still be processing."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId, urlPlanId, currentUser, planHint.name, isWriteComplete]);

  if (loading) {
    return (
      <div className="cs-root cs-gradient">
        <div className="cs-card cs-blur">
          <h2 className="cs-title">Processing your payment…</h2>
          <p className="cs-muted">
            Please hold on while we confirm your subscription.
          </p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="cs-root cs-gradient">
        <div className="cs-card cs-blur">
          <h2 className="cs-title">Payment Received</h2>
          <p className="cs-muted">{err}</p>

          {(debugStatus || debugBody) && (
            <pre className="cs-debug">
              {`status: ${debugStatus ?? "n/a"}\nbody: \n${
                debugBody || "(empty)"
              }`}
            </pre>
          )}

          <div className="cs-actions cs-actions-3">
            <a href="/my-nutrition-status" className="btn btn-primary">
              Go to My AI Nutrition
            </a>
            <button
              className="btn btn-secondary"
              onClick={(event) => {
                const url = "/account/billing";
                if (event.ctrlKey || event.metaKey) {
                  window.open(url, "_blank");
                } else {
                  window.location.href = url;
                }
              }}
            >
              Manage Subscription
            </button>
            <a href="/pricing" className="btn btn-secondary">
              Back to Pricing
            </a>
          </div>
        </div>
      </div>
    );
  }

  const payerMasked = maskEmail(info?.payerEmail || currentUser?.email);

  return (
    <div className="cs-root cs-gradient">
      <div className="cs-card cs-blur">
        <div className="cs-header">
          <div className="cs-check" aria-hidden="true">
            <svg
              className="checkmark"
              viewBox="0 0 52 52"
              role="img"
              aria-label="Success"
            >
              <circle
                className="checkmark__circle"
                cx="26"
                cy="26"
                r="25"
                fill="none"
              ></circle>
              <path
                className="checkmark__check"
                fill="none"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              ></path>
            </svg>
          </div>

          <div role="status" aria-live="polite">
            <h1 id="main-heading" tabIndex={-1} className="cs-hero">
              Subscription Activated!
            </h1>
          </div>

          <p className="cs-chip">
            <span className="cs-chip-dot" />
            ACTIVE
          </p>

          <p className="cs-sub">
            Thank you for subscribing. Your plan is now active and ready to use.
          </p>
        </div>

        <section aria-labelledby="summary-title" className="cs-section">
          <h2 id="summary-title" className="cs-section-title">
            Subscription Summary
          </h2>

          <div className="cs-planbox">
            <div className="cs-planrow">
              <div className="cs-planleft">
                <p className="cs-planname" title={info?.planName || ""}>
                  {info?.planName || "-"}
                </p>
                <p className="cs-planid">Your Plan</p>
              </div>
              <p className="cs-amount">
                {formatMoney(info?.price, info?.currency)}
              </p>
            </div>
          </div>

          <div className="cs-kv">
            <div className="cs-kvrow">
              <span className="cs-kvkey">Subscription ID</span>
              <span className="cs-kvval mono" title={info?.id || ""}>
                {info?.id || "-"}
              </span>
            </div>
            <div className="cs-kvrow">
              <span className="cs-kvkey">Billed to</span>
              <span className="cs-kvval">{payerMasked}</span>
            </div>
            <div className="cs-kvrow">
              <span className="cs-kvkey">Next billing</span>
              <span className="cs-kvval">
                {formatDate(info?.nextBilling)}
              </span>
            </div>
            <div className="cs-kvrow">
              <span className="cs-kvkey">Renewal amount</span>
              <span className="cs-kvval">
                {formatMoney(info?.price, info?.currency)}
              </span>
            </div>
          </div>
        </section>

        <div className="cs-actions cs-actions-3">
          <a href="/my-nutrition-status" className="btn btn-primary">
            Go to My AI Nutrition
          </a>
          <button
            className="btn btn-secondary"
            onClick={(event) => {
              const url = "/account/billing";
              if (event.ctrlKey || event.metaKey) {
                window.open(url, "_blank");
              } else {
                window.location.href = url;
              }
            }}
          >
            Manage Subscription
          </button>
          <SubscriptionInvoicePdfDownLoad
            data={info}
            payerEmail={info?.payerEmail || currentUser?.email}
          />
        </div>

        <footer className="cs-footer">
          <div className="cs-fine">
            <p>© {new Date().getFullYear()} Pure Health Nexus</p>
          </div>
          <p className="cs-policy">
            <a href="/refund-policy">Refund &amp; Cancellation Policy</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
